import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import Loading from "@/components/Common/Loading";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEncounter } from "@/pages/Encounters/utils/EncounterProvider";
import { MVPImmunizationRecordCreate } from "@/types/mvp/immunization";
import immunizationApi from "@/types/mvp/immunizationApi";
import query, { callApi } from "@/Utils/request/query";

const getDefaultAdministeredAt = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000)
    .toISOString()
    .slice(0, 16);
};

const emptyForm = (): MVPImmunizationRecordCreate => ({
  patient: "",
  encounter: "",
  vaccine: "",
  dose: "",
  route: "",
  site: "",
  lot_number: "",
  expiry_date: "",
  administered_at: getDefaultAdministeredAt(),
});

export const EncounterImmunizationsTab = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    facilityId,
    patientId,
    selectedEncounterId: encounterId,
    canWriteSelectedEncounter,
  } = useEncounter();
  const [form, setForm] = useState<MVPImmunizationRecordCreate>(() => ({
    ...emptyForm(),
    patient: patientId,
    encounter: encounterId,
  }));

  useEffect(() => {
    setForm({
      ...emptyForm(),
      patient: patientId,
      encounter: encounterId,
    });
  }, [encounterId, patientId]);

  const { data, isLoading } = useQuery({
    queryKey: ["mvp-immunizations", facilityId, patientId],
    queryFn: query(immunizationApi.list, {
      pathParams: { facilityId: facilityId || "" },
      queryParams: { patient: patientId, limit: 50, offset: 0 },
    }),
    enabled: !!facilityId && !!patientId,
  });

  const { mutate: createImmunization, isPending } = useMutation({
    mutationFn: (body: MVPImmunizationRecordCreate) =>
      callApi(immunizationApi.create, {
        pathParams: { facilityId: facilityId || "" },
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mvp-immunizations", facilityId, patientId],
      });
      setForm({
        ...emptyForm(),
        patient: patientId,
        encounter: encounterId,
      });
      toast.success(t("mvp_immunization_saved"));
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : t("mvp_immunization_save_failed"),
      );
    },
  });

  const updateField = (
    field: keyof MVPImmunizationRecordCreate,
    value: string,
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.vaccine.trim() || !form.dose.trim() || !form.administered_at) {
      toast.error(t("mvp_immunization_required_fields"));
      return;
    }
    createImmunization({
      ...form,
      administered_at: new Date(form.administered_at).toISOString(),
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-4 py-4">
      {canWriteSelectedEncounter && (
        <Card>
          <CardHeader>
            <CardTitle>{t("mvp_record_immunization")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
              <div className="space-y-1">
                <Label htmlFor="immunization-vaccine">
                  {t("mvp_vaccine")} *
                </Label>
                <Input
                  id="immunization-vaccine"
                  value={form.vaccine}
                  onChange={(event) =>
                    updateField("vaccine", event.target.value)
                  }
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="immunization-dose">{t("mvp_dose")} *</Label>
                <Input
                  id="immunization-dose"
                  value={form.dose}
                  onChange={(event) => updateField("dose", event.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="immunization-administered-at">
                  {t("mvp_administered_at")} *
                </Label>
                <Input
                  id="immunization-administered-at"
                  type="datetime-local"
                  value={form.administered_at}
                  onChange={(event) =>
                    updateField("administered_at", event.target.value)
                  }
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="immunization-route">{t("mvp_route")}</Label>
                <Input
                  id="immunization-route"
                  value={form.route}
                  onChange={(event) => updateField("route", event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="immunization-site">{t("mvp_site")}</Label>
                <Input
                  id="immunization-site"
                  value={form.site}
                  onChange={(event) => updateField("site", event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="immunization-lot">{t("mvp_lot_number")}</Label>
                <Input
                  id="immunization-lot"
                  value={form.lot_number}
                  onChange={(event) =>
                    updateField("lot_number", event.target.value)
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="immunization-expiry">
                  {t("mvp_expiry_date")}
                </Label>
                <Input
                  id="immunization-expiry"
                  type="date"
                  value={form.expiry_date}
                  onChange={(event) =>
                    updateField("expiry_date", event.target.value)
                  }
                />
              </div>
              <div className="flex items-end md:justify-end">
                <Button type="submit" disabled={isPending}>
                  {isPending ? t("saving") : t("mvp_save_immunization")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("mvp_immunization_history")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.results?.length ? (
            <div className="space-y-3">
              {data.results.map((record) => (
                <div
                  key={record.id}
                  className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{record.vaccine}</span>
                    <span className="text-sm text-gray-600">{record.dose}</span>
                    <span className="text-sm text-gray-600">
                      {new Date(record.administered_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    {[record.route, record.site, record.lot_number]
                      .filter(Boolean)
                      .join(" · ") || t("mvp_no_administering_context")}
                  </div>
                  {record.expiry_date && (
                    <div className="mt-1 text-xs text-gray-500">
                      {t("mvp_expiry_date")}: {record.expiry_date}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              {t("mvp_no_immunizations")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
